/**
 * CommercialProductDetail — detail panel for a selected commercial product.
 *
 * Renders the product description and dispatches on `calculator.kind` to
 * the correct sub-component. Reads optional `delta` and `unit` query params
 * for Phase 4 pre-fill from the "Dose to Fix" workflow.
 */
import type { CommercialProduct } from '@reefapp/shared';
import { SimpleVolumeCalc } from './calculators/SimpleVolumeCalc';
import { AreaCalc } from './calculators/AreaCalc';
import { StaticTextCalc } from './calculators/StaticTextCalc';
import { ComplexTextCalc } from './calculators/ComplexTextCalc';
import { ParameterAdjustmentCalc } from './calculators/ParameterAdjustmentCalc';

export interface CommercialProductDetailProps {
  product: CommercialProduct;
  /** Tank volume in US gallons (0 = not set). */
  tankVolumeGal: number;
  /** Pre-fill delta from Phase 4 "Dose to Fix" deep-link. */
  deltaParam?: string;
  /** Pre-fill unit from Phase 4 deep-link. */
  deltaUnit?: string;
}

export function CommercialProductDetail({
  product,
  tankVolumeGal,
  deltaParam,
  deltaUnit,
}: CommercialProductDetailProps) {
  const paramsText =
    Array.isArray(product.parameter)
      ? product.parameter.join(', ')
      : product.parameter;

  const infoRows: ReadonlyArray<readonly [string, string | undefined]> = [
    ['Directions', product.directions],
    ['Frequency', product.frequency],
    ['Preparation', product.prep],
    ['Target / Duration', product.targetRange],
  ];
  const hasInfo = infoRows.some(([, value]) => Boolean(value)) || Boolean(product.sourceUrl);


  const renderCalculator = () => {
    const calc = product.calculator;
    switch (calc.kind) {
      case 'simple_volume':
        return (
          <SimpleVolumeCalc
            calculator={calc}
            tankVolumeGal={tankVolumeGal}
          />
        );
      case 'area':
        return (
          <AreaCalc
            calculator={calc}
            areaSqIn={tankVolumeGal > 0 ? undefined : undefined}
          />
        );
      case 'static_text':
        return <StaticTextCalc calculator={calc} />;
      case 'complex_text':
        return (
          <ComplexTextCalc
            calculator={calc}
            tankVolumeGal={tankVolumeGal}
          />
        );
      case 'parameter_adjustment':
        return (
          <ParameterAdjustmentCalc
            calculator={calc}
            tankVolumeGal={tankVolumeGal}
            prefillDelta={deltaParam}
            prefillUnit={deltaUnit}
          />
        );
    }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 shadow-lg">
      {/* Product header */}
      <div className="mb-6">
        <p className="text-sm text-cyan-400 font-medium">{product.brand}</p>
        <h2 className="text-2xl font-bold text-slate-100 mt-1">{product.name}</h2>
        <p className="text-sm text-slate-400 mt-2">{paramsText}</p>
        <p className="mt-4 text-slate-300 leading-relaxed">
          {product.description}
        </p>
      </div>

      {product.warnings && (
        <div className="mb-6 p-4 rounded-lg bg-red-900 border border-red-500 text-red-300 text-sm">
          {product.warnings}
        </div>
      )}

      {/* Divider */}
      <hr className="border-slate-700 mb-6" />

      {/* Calculator */}
      {renderCalculator()}

      {/* Structured product info */}
      {hasInfo && (
        <div className="mt-6 space-y-3">
          {infoRows.map(([label, value]) =>
            value ? (
              <div key={label}>
                <p className="text-sm font-semibold text-cyan-300">{label}</p>
                <p className="text-sm text-slate-300 leading-relaxed">{value}</p>
              </div>
            ) : null,
          )}
          {product.sourceUrl && (
            <p className="text-xs text-slate-500">
              Source:{' '}
              <a
                href={product.sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="text-cyan-400 hover:underline break-all"
              >
                {product.sourceUrl}
              </a>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
